<template>
  <EXTooltip
    placement="top"
    class="position-capacity-trigger__tooltip"
    :title="tooltipTitle"
    :disabled="!tooltipTitle"
    :delay="300"
    show-on-child-focus
  >
    <button
      type="button"
      class="position-capacity-trigger"
      :class="{
        'position-capacity-trigger--open': isOpen,
        'position-capacity-trigger--disabled': isDisabled,
      }"
      :aria-label="ariaLabel"
      :aria-expanded="isDisabled ? undefined : String(isOpen)"
      :aria-disabled="isDisabled ? 'true' : undefined"
      aria-haspopup="dialog"
      data-name="position-capacity-trigger"
      @click="handleClick"
    >
      <CapacityIconWithoutSelector
        class="position-capacity-trigger__icon"
        size="normal"
        :value="capacity"
        :occupied="allocation"
      />
      <template v-if="!isDisabled">
        <ChevronUpIcon v-if="isOpen" class="position-capacity-trigger__chevron" />
        <ChevronDownIcon v-else class="position-capacity-trigger__chevron" />
      </template>
    </button>
  </EXTooltip>
</template>

<script setup>
import { computed } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import CapacityIconWithoutSelector from "@components/organizations/positions/CapacityIconWithoutSelector.vue";
import ChevronDownIcon from "@components/icons/items/Generic/ChevronDownIcon.vue";
import ChevronUpIcon from "@components/icons/items/Generic/ChevronUpIcon.vue";

const props = defineProps({
  /** Position capacity in FTE: 1 or 0.5. */
  capacity: {
    type: Number,
    default: 1,
    validator: (value) => [1, 0.5].includes(value),
  },
  /** Occupied portion: "not" (Not occupied), "0.5", "2x0.5" or "1". Anything but "not" disables the trigger. */
  allocation: {
    type: String,
    default: "not",
    validator: (value) => ["not", "0.5", "2x0.5", "1"].includes(value),
  },
  /** The Capacity Selector is open: shows the Open state with the chevron up. */
  open: {
    type: Boolean,
    default: false,
  },
  /** Force the Disabled state (e.g. Retired / Archived positions). */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Tooltip shown while the trigger is disabled. Empty string hides it. */
  disabledTooltip: {
    type: String,
    default: "Unassign employees to change capacity.",
  },
});

const emit = defineEmits(["click"]);

const isDisabled = computed(() => props.disabled || props.allocation !== "not");
const isOpen = computed(() => props.open && !isDisabled.value);
const tooltipTitle = computed(() => (isDisabled.value ? props.disabledTooltip : ""));

const ariaLabel = computed(() => {
  const occupied = props.allocation === "not" ? "not occupied" : `${props.allocation} occupied`;
  const label = `Capacity ${props.capacity} FTE, ${occupied}`;
  return tooltipTitle.value ? `${label}. ${tooltipTitle.value}` : label;
});

const handleClick = (event) => {
  if (isDisabled.value) return;
  emit("click", event);
};
</script>

<style scoped>
.position-capacity-trigger__tooltip {
  display: inline-flex;
}

.position-capacity-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  box-sizing: border-box;
  width: 46px;
  height: 28px;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.position-capacity-trigger:hover {
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #7f56d9;
}

.position-capacity-trigger:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.position-capacity-trigger--open,
.position-capacity-trigger--open:hover {
  background: #f2edff;
  box-shadow: inset 0 0 0 1.5px #7f56d9;
}

.position-capacity-trigger--disabled,
.position-capacity-trigger--disabled:hover {
  width: 28px;
  background: transparent;
  box-shadow: none;
  cursor: not-allowed;
}

.position-capacity-trigger__icon {
  flex: 0 0 auto;
  pointer-events: none;
}

.position-capacity-trigger__chevron {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  pointer-events: none;
}
</style>
